import { useMemo, useState } from "react";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const sections = [
  {
    id: "basics",
    label: "Brand basics",
    sectionLabel: "Section 1 of 5",
    title: "Brand basics",
    description: "Start with the foundational facts. Even obvious answers help AI generate precise content.",
    fields: [
      {
        key: "brand_name",
        label: "Brand / client name",
        helper: "What do they go by — official name and any shorter working name.",
      },
      {
        key: "industry_category",
        label: "Industry and category",
        helper: "e.g. B2B IT services, D2C skincare, luxury fashion, wellness studio...",
      },
      {
        key: "primary_markets",
        label: "Primary market(s)",
        helper: "India only? Pan-India? Specific cities? International?",
      },
    ],
  },
  {
    id: "story",
    label: "Brand story",
    sectionLabel: "Section 2 of 5",
    title: "Brand story",
    description: "Define why the brand exists and what proof supports that narrative.",
    fields: [
      { key: "origin_story", label: "Origin story", helper: "Founding context and motivation." },
      { key: "value_prop", label: "Core value proposition", helper: "What unique value is delivered?" },
      { key: "proof_points", label: "Proof points", helper: "Metrics, awards, testimonials, client wins." },
    ],
  },
  {
    id: "audience",
    label: "Audience",
    sectionLabel: "Section 3 of 5",
    title: "Audience",
    description: "Specify whom you're speaking to and what matters to them.",
    fields: [
      { key: "primary_persona", label: "Primary persona", helper: "Role, age group, context, location." },
      { key: "secondary_persona", label: "Secondary persona", helper: "Who influences decisions?" },
      { key: "pain_points", label: "Pain points", helper: "Top concerns and blockers." },
    ],
  },
  {
    id: "voice",
    label: "Brand voice",
    sectionLabel: "Section 4 of 5",
    title: "Brand voice",
    description: "Capture tone and style guardrails for all channels.",
    fields: [
      { key: "tone_words", label: "Tone words", helper: "e.g. expert, warm, no fluff, direct." },
      { key: "words_to_avoid", label: "Words to avoid", helper: "Language to avoid in public content." },
      { key: "sample_line", label: "Sample line", helper: "One sentence that sounds like your brand." },
    ],
  },
  {
    id: "goals",
    label: "Goals & KPIs",
    sectionLabel: "Section 5 of 5",
    title: "Goals & KPIs",
    description: "Define measurable outcomes and accountability.",
    fields: [
      { key: "business_goals", label: "Business goals", helper: "Revenue, retention, expansion targets." },
      { key: "marketing_kpis", label: "Marketing KPIs", helper: "Traffic, lead quality, conversion, CAC." },
      { key: "time_horizon", label: "Time horizon", helper: "30 / 60 / 90 day expectations." },
    ],
  },
  {
    id: "export",
    label: "Export brief",
    sectionLabel: "Summary",
    title: "Export brief",
    description: "Copy your final brief and AI prompt.",
    fields: [],
  },
];

const businessModels = ["B2B", "B2C", "D2C", "SaaS / product", "Service-based", "Ecommerce", "Marketplace", "Hybrid"];
const businessStages = ["Pre-revenue", "Growing (₹1–10Cr)", "Scaling (₹10–100Cr)", "Established (₹100Cr+)", "Enterprise", "Not sure"];

const sanitizeTestId = (value) => String(value).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();

const chipButtonStyle = (isSelected, isHovered) => ({
  fontSize: 12,
  padding: "5px 12px",
  borderRadius: 999,
  border: `1px solid ${isSelected || isHovered ? "var(--brand-600)" : "var(--paper-300)"}`,
  background: isSelected ? "var(--brand-600)" : isHovered ? "#EEF2FF" : "transparent",
  color: isSelected ? "#fff" : isHovered ? "var(--brand-600)" : "var(--mute-600)",
  cursor: "pointer",
  transition: "all .12s",
  boxShadow: isHovered && !isSelected ? "0 0 0 1px rgba(79,70,229,.08)" : "none",
});

export default function BriefBuilderPage({ user, onLogout }) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedBusinessModel, setSelectedBusinessModel] = useState("D2C");
  const [selectedBusinessStage, setSelectedBusinessStage] = useState("");
  const [hoveredBusinessModel, setHoveredBusinessModel] = useState("");
  const [hoveredBusinessStage, setHoveredBusinessStage] = useState("");

  const activeSection = sections[activeSectionIndex];
  const visibleSectionsCount = 5;
  const visualStep = Math.min(activeSectionIndex + 1, visibleSectionsCount);
  const progress = Math.round((visualStep / visibleSectionsCount) * 100);

  const briefOutput = useMemo(() => {
    const lines = [];
    sections.slice(0, 5).forEach((section) => {
      lines.push(`## ${section.title}`);
      section.fields.forEach((field) => {
        lines.push(`- ${field.label}: ${answers[field.key] || ""}`);
      });
      if (section.id === "basics") {
        lines.push(`- Business model: ${selectedBusinessModel || ""}`);
        lines.push(`- Business stage: ${selectedBusinessStage || ""}`);
      }
      lines.push("");
    });
    return lines.join("\n");
  }, [answers, selectedBusinessModel, selectedBusinessStage]);

  const aiPrompt = useMemo(
    () =>
      `Use this brand brief to generate a 90-day marketing strategy with channel priorities, campaign themes, content pillars, message matrix, and weekly execution checkpoints:\n\n${briefOutput}`,
    [briefOutput],
  );

  const handleCopy = async (value, successText) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successText);
    } catch (_error) {
      toast.error("Unable to copy");
    }
  };

  const handleNext = () => setActiveSectionIndex((prev) => Math.min(sections.length - 1, prev + 1));
  const handleBack = () => setActiveSectionIndex((prev) => Math.max(0, prev - 1));

  return (
    <AppShell user={user} onLogout={onLogout} topbarSearchPlaceholder="Search tasks, projects...">
      <section className="mx-auto w-full max-w-[700px] space-y-0" data-testid="brief-page-root">
        <div style={{ marginBottom: 24 }} data-testid="brief-header-card">
          <div className="flex items-center gap-2" style={{ marginBottom: 4 }} data-testid="brief-header-row">
            <span className="chip chip-brand" style={{ fontSize: 11, fontWeight: 600 }} data-testid="brief-tool-chip">Tool</span>
            <h1 className="page-title" data-testid="brief-header-title">Brand Brief Builder</h1>
          </div>
          <p className="page-sub" data-testid="brief-header-subtitle">
            Fill out each section to generate a client brief and AI content strategy prompt.
          </p>
        </div>

        <div style={{ height: 3, background: "var(--paper-200)", borderRadius: 99, marginBottom: 24, overflow: "hidden" }} data-testid="brief-progress-track">
          <div style={{ width: `${progress}%`, height: "100%", background: "var(--brand-600)", borderRadius: 99 }} data-testid="brief-progress-fill" />
        </div>

        <div className="flex flex-wrap" style={{ gap: 6, marginBottom: 24 }} data-testid="brief-tabs-wrap">
          {sections.map((section, index) => {
            const isActive = index === activeSectionIndex;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSectionIndex(index)}
                className="jump-pill-button"
                style={{
                  fontSize: 12,
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: `1px solid ${isActive ? "var(--ink-900)" : "var(--paper-300)"}`,
                  background: isActive ? "var(--ink-900)" : "transparent",
                  color: isActive ? "#fff" : "var(--mute-600)",
                  fontWeight: isActive ? 500 : 400,
                }}
                data-testid={`brief-section-tab-${section.id}`}
              >
                {section.label}
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--brand-600)", marginBottom: 4 }} data-testid="brief-section-progress-label">
          {activeSection.sectionLabel}
        </div>

        {activeSection.id !== "export" ? (
          <div data-testid={`brief-section-content-${activeSection.id}`}>
            <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }} data-testid="brief-section-title">{activeSection.title}</h2>
            <p style={{ fontSize: 13.5, color: "var(--mute-600)", marginBottom: 24 }} data-testid="brief-section-description">{activeSection.description}</p>

            {activeSection.fields.map((field) => (
              <div key={field.key} className="card" style={{ padding: "16px 18px", marginBottom: 10 }} data-testid={`brief-question-card-${sanitizeTestId(field.key)}`}>
                <label style={{ fontSize: 13.5, fontWeight: 600, display: "block" }} data-testid={`brief-question-label-${sanitizeTestId(field.key)}`}>
                  {field.label}
                </label>
                <div style={{ fontSize: 12, color: "var(--mute-400)", marginTop: 2 }} data-testid={`brief-question-helper-${sanitizeTestId(field.key)}`}>
                  {field.helper}
                </div>
                <Input
                  value={answers[field.key] || ""}
                  onChange={(event) => setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))}
                  placeholder="Type your answer..."
                  className="field-input"
                  style={{ marginTop: 8 }}
                  data-testid={`brief-input-${sanitizeTestId(field.key)}`}
                />
              </div>
            ))}

            {activeSection.id === "basics" ? (
              <>
                <div className="card" style={{ padding: "16px 18px", marginBottom: 10 }} data-testid="brief-business-model-card">
                  <label style={{ fontSize: 13.5, fontWeight: 600, display: "block" }} data-testid="brief-business-model-label">Business model</label>
                  <div style={{ fontSize: 12, color: "var(--mute-400)", marginTop: 2 }} data-testid="brief-business-model-helper">Select all that apply.</div>
                  <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }} data-testid="brief-business-model-options-wrap">
                    {businessModels.map((item) => {
                      const isSelected = selectedBusinessModel === item;
                      const isHovered = hoveredBusinessModel === item;
                      return (
                        <button
                          key={item}
                          type="button"
                          style={chipButtonStyle(isSelected, isHovered)}
                          onMouseEnter={() => setHoveredBusinessModel(item)}
                          onMouseLeave={() => setHoveredBusinessModel("")}
                          onClick={() => setSelectedBusinessModel(item)}
                          data-testid={`brief-business-model-chip-${sanitizeTestId(item)}`}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="card" style={{ padding: "16px 18px", marginBottom: 10 }} data-testid="brief-business-stage-card">
                  <label style={{ fontSize: 13.5, fontWeight: 600, display: "block" }} data-testid="brief-business-stage-label">Business stage</label>
                  <div style={{ fontSize: 12, color: "var(--mute-400)", marginTop: 2 }} data-testid="brief-business-stage-helper">This affects tone and content ambition.</div>
                  <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }} data-testid="brief-business-stage-options-wrap">
                    {businessStages.map((item) => {
                      const isSelected = selectedBusinessStage === item;
                      const isHovered = hoveredBusinessStage === item;
                      return (
                        <button
                          key={item}
                          type="button"
                          style={chipButtonStyle(isSelected, isHovered)}
                          onMouseEnter={() => setHoveredBusinessStage(item)}
                          onMouseLeave={() => setHoveredBusinessStage("")}
                          onClick={() => setSelectedBusinessStage(item)}
                          data-testid={`brief-business-stage-chip-${sanitizeTestId(item)}`}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4" data-testid="brief-export-screen">
            <div className="card" style={{ padding: 16 }} data-testid="brief-export-brief-card">
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }} data-testid="brief-export-brief-title">Client brief</h3>
              <div className="max-h-[320px] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm whitespace-pre-wrap" data-testid="brief-export-brief-content">
                {briefOutput}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={() => handleCopy(briefOutput, "Brief copied")} data-testid="brief-export-copy-brief-button">Copy brief</Button>
                <Button variant="outline" onClick={() => handleCopy(aiPrompt, "AI prompt copied")} data-testid="brief-export-copy-prompt-button">Copy AI prompt</Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between" style={{ marginTop: 24, marginBottom: 12 }} data-testid="brief-navigation-row">
          <div data-testid="brief-back-button-slot">
            {activeSectionIndex > 0 ? (
              <Button variant="outline" onClick={handleBack} data-testid="brief-back-button">← Back</Button>
            ) : null}
          </div>

          <Button
            onClick={handleNext}
            disabled={activeSectionIndex === sections.length - 1}
            style={{
              height: 56,
              padding: "0 20px",
              borderRadius: 14,
              background: "#030C22",
              borderColor: "#030C22",
              color: "#fff",
              fontSize: 16,
              lineHeight: 1,
              fontWeight: 500,
            }}
            data-testid="brief-next-button"
          >
            Next →
          </Button>
        </div>
      </section>
    </AppShell>
  );
}
